import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
TablePagination,
Menu,
Avatar,
CardMedia,
FormControlLabel,
Switch,
RadioGroup,
Radio,
FormLabel
} from '@mui/material';
import { 
Delete, 
People, 
PictureAsPdf,
TableChart, 
GetApp,
Person,
Email,
Phone,
CalendarToday,
Work,
Business,
Category,
AccountTree,
AttachMoney,
Schedule
} from '@mui/icons-material';

import {
Close as CloseIcon,
Add as AddIcon,
Search as SearchIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon,
Upload as UploadIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../../hooks/useToast';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
formatCreatedAtDate, 
formatNumberWithComma, 
formatNumberWithComma2, 
getCategoriesNameById, 
getStatusById, 
getUserDetails, 
recordsPerPage, 
statusData 
} from '../../../utils/helpers';
import { 
exportEmployeesToCSV, 
exportEmployeesToExcel, 
exportEmployeesToPDFEnhanced 
} from '../../../services/EmployeeService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { dutyTypeOptions, genderOptions, payFrequencyOptions } from '../../../utils/dataInfo';
import { APIEmployeeImage } from '../../../services/APIs';
import { employeesApiService } from '../../../apiServices/EmployeesApiServices';
import { branchApiService } from '../../../apiServices/BranchApiService';
import { departmentApiService } from '../../../apiServices/DepartmentApiService';
import { positionApiService } from '../../../apiServices/PositionApiService';

// Helper function to get initials from employee name
const getInitials = (name) => {
if (!name) return '?';
return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Helper function to get image URL
const getImageUrl = (employee) => {
if (employee.image_url) {
    return employee.image_url;
}
if (employee.image) {
    // If image is stored as relative path in database
    if (employee.image.startsWith('images/employee/')) {
    return `${window.location.origin}/${employee.image}`;
    }
    // If image is just a filename
    return `${APIEmployeeImage}${employee.image}`;
}
return null; // No image
};

// Helper function to get avatar color based on employee name
const stringToColor = (string) => {
let hash = 0;
for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
}
let color = '#';
for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
}
return color;
};

const Employee = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [pageState, setPageState] = useState({
    pageData: [],
});
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
const [formData, setFormData] = useState({
    employee_name: '',
    email: '',
    contact: '',
    gender: '',
    position_id: '',
    department_id: '',
    start_date: '',
    duty_type: '',
    pay_frequency: '',
    additional_info: '',
    status: '1',
    branch_id: '',
    image: null
});
const [filters, setFilters] = useState({
    employee_name: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    department_id: '',
    position_id: ''
});
const [errors, setErrors] = useState({
    employee_name: false,
    email: false,
    contact: false,
    position_id: false,
    department_id: false,
    status: false
});

// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [branchNamesFilterData, setBranchNamesFilterData] = useState([]);
const [selectedBranch, setSelectedBranch] = useState('');
const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

// API data for selections
const [departmentNamesData, setDepartmentNamesData] = useState([]);
const [positionNamesData, setPositionNamesData] = useState([]);
const [divisionNamesData, setDivisionNamesData] = useState([]);

// Image preview
const [imagePreview, setImagePreview] = useState(null);

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    employeesApiService.setToastFunction(showToast);
    departmentApiService.setToastFunction(showToast);
    positionApiService.setToastFunction(showToast);
}, [showToast]);

useEffect(() => {
    fetchData();
    fetchBranchNames();
    fetchDepartmentNames();
    fetchPositionNames();
}, []);

const fetchBranchNames = async () => {
    try {
    const response = await branchApiService.listNames();
    if (response.success) {
        setBranchNamesFilterData(response.data || []);
    } else {
        showToast("Error", response.message || "Failed to load branch names", "error");
    }
    } catch (error) {
    console.error("Error fetching branch names:", error);
    showToast("Error", "Failed to load branch names", "error");
    }
};

const fetchDepartmentNames = async (branchId = null) => {
    try {
        let branchIdToUse = branchId;
        // If no branchId provided, determine based on permissions and selection
        if (!branchIdToUse) {
            if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
                // Use the selected branch for super admin
                branchIdToUse = selectedBranch;
            } else if (!hasPermission(PERMISSIONS.BRANCHES)) {
                // Use current user's branch for non-super admin
                branchIdToUse = getUserDetails("branchId");
            }
        }
        // Call the service with branchId
        const response = await departmentApiService.listNames(branchIdToUse);
        if (response.success) {
            setDepartmentNamesData(response.data || []);
            return response.data || [];
        } else {
            showToast("Error", response.message || "Failed to load departments", "error");
            return [];
        }
    } catch (error) {
        console.error("Error fetching department names:", error);
        showToast("Error", "Failed to fetch department names", "error");
        return [];
    }
};
const  fetchPositionNames = async (branchId = null) => {
    try {
        let branchIdToUse = branchId;
        // If no branchId provided, determine based on permissions and selection
        if (!branchIdToUse) {
            if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
                // Use the selected branch for super admin
                branchIdToUse = selectedBranch;
            } else if (!hasPermission(PERMISSIONS.BRANCHES)) {
                // Use current user's branch for non-super admin
                branchIdToUse = getUserDetails("branchId");
            }
        }
        // Call the service with branchId
        const response = await positionApiService.listNames(branchIdToUse);
        if (response.success) {
            setPositionNamesData(response.data || []);
            return response.data || [];
        } else {
            showToast("Error", response.message || "Failed to load position", "error");
            return [];
        }
    } catch (error) {
        console.error("Error fetching position names:", error);
        showToast("Error", "Failed to fetch position names", "error");
        return [];
    }
};


const fetchData = async (branchId = null) => {
    setLoading(true);
    setError(null);

    try {
    let branchIdToUse = branchId;
    if (!hasPermission(PERMISSIONS.BRANCHES)) {
        branchIdToUse = getUserDetails("branchId");
    }

    const response = await employeesApiService.list(branchIdToUse);
    if (response.success) {
        const employees = Array.isArray(response.data) ? response.data.reverse() : [];

        setPageState(prev => ({
            ...prev,
            pageData: employees,
        }));

        showToast("Success", TEXT_DATA_LOADED, "success");
    } else {
        setError(response.message);
        showToast("Error", response.message, "error");
    }
    } catch (error) {
    let errorMessage = TEXT_CONNECTION_ERROR;
    setError(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setBranchSelectionOpen(false);
    fetchData(branchId);
};

// Filter employees based on search term and filters
const filteredData = pageState.pageData.filter(employee => {
    const matchesSearch = searchTerm === '' ||
    employee.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.contact.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesName = !filters.employee_name || 
    employee.employee_name.toLowerCase().includes(filters.employee_name.toLowerCase());

    const matchesStatus = !filters.status || 
    employee.status.toString() === filters.status;

    const matchesDepartment = !filters.department_id || 
    employee.department_id.toString() === filters.department_id;

    const matchesPosition = !filters.position_id || 
    employee.position_id.toString() === filters.position_id;

    let matchesDate = true;
    if (filters.dateFrom) {
    const employeeDate = new Date(employee.start_date || employee.created_at).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && employeeDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const employeeDate = new Date(employee.start_date || employee.created_at).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && employeeDate <= filterToDate;
    }

    return matchesSearch && matchesName && matchesStatus && matchesDepartment && matchesPosition && matchesDate;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handlefilterOptions = () => setFilterOptions(true);
const handleCloseOptions = () => setFilterOptions(false);

const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    employee_name: '',
    email: '',
    contact: '',
    gender: '',
    position_id: '',
    department_id: '',
    start_date: '',
    duty_type: '',
    pay_frequency: '',
    additional_info: '',
    status: '1',
    branch_id: selectedBranch || '',
    image: null
    });
    setImagePreview(null); // Reset image preview
    setOpenAdd(true);
};

const handleCloseAdd = () => {
    setOpenAdd(false);
};

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");
    setFormData({
    employee_name: record.employee_name,
    email: record.email,
    contact: record.contact,
    gender: record.gender,
    position_id: record.position_id,
    department_id: record.department_id,
    start_date: record.start_date ? new Date(record.start_date).toISOString().split('T')[0] : '',
    duty_type: record.duty_type,
    pay_frequency: record.pay_frequency,
    additional_info: record.additional_info,
    status: record.status.toString(),
    branch_id: record.branch_id,
    image: null
    });
    
    // Set image preview
    const imageUrl = getImageUrl(record);
    if (imageUrl) {
    setImagePreview(imageUrl);
    } else {
    setImagePreview(null);
    }
    
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
    setFormData(prev => ({
        ...prev,
        image: file
    }));
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
        setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    }
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    handleCloseOptions();
};

const clearFilters = () => {
    setFilters({
    employee_name: '',
    dateFrom: '',
    dateTo: '',
    status: '',
    department_id: '',
    position_id: ''
    });
    setSearchTerm('');
    handleCloseOptions();
};

const validateForm = () => {
    const errors = [];
    if (!formData.employee_name.trim()) errors.push('Employee Name is required');
    if (!formData.email.trim()) errors.push('Email is required');
    if (!formData.contact.trim()) errors.push('Contact is required');
    if (!formData.position_id) errors.push('Position is required');
    if (!formData.department_id) errors.push('Department is required');
    if (!formData.status) errors.push('Status is required');

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
    errors.push('Please enter a valid email address');
    }

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleDialogClose = () => {
    setOpenAdd(false);
    setOpenView(false);
    setOpenDelete(false);
    setFilterOptions(false);
};

const handleFormDataReset = () => {
    setFormData({
    employee_name: '',
    email: '',
    contact: '',
    gender: '',
    position_id: '',
    department_id: '',
    start_date: '',
    duty_type: '',
    pay_frequency: '',
    additional_info: '',
    status: '1',
    branch_id: selectedBranch || '',
    image: null
    });
    setImagePreview(null);
};

// Export handlers
const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

const handleExportExcel = async () => {
    try {
    handleExportClose();
    await exportEmployeesToExcel(filteredData, {
        departmentNamesData,
        positionNamesData,
        divisionNamesData
    });
    showToast("Success", "Excel file downloaded successfully", "success");
    } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportEmployeesToPDFEnhanced(filteredData, {
        departmentNamesData,
        positionNamesData,
        divisionNamesData
    }, {
        employee_name: filters.employee_name,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        status: filters.status,
        searchTerm: searchTerm
    });
    showToast("Success", "PDF file downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportEmployeesToCSV(filteredData, {
        departmentNamesData,
        positionNamesData,
        divisionNamesData
    });
    showToast("Success", "CSV file downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};

const getSelectedBranchName = () => {
    if (!selectedBranch) return "Select Branch";
    const branch = branchNamesFilterData.find(b => b.id === selectedBranch);
    return branch ? `${branch.name} (${branch.code})` : "Select Branch";
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
        if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('user_id', getUserDetails("Id"));

    // Set branch_id based on permissions
    if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
        formDataToSend.append('branch_id', selectedBranch);
    } else {
        formDataToSend.append('branch_id', getUserDetails("branchId"));
    }

    // Append required fields
    formDataToSend.append('employee_name', formData.employee_name || '');
    formDataToSend.append('email', formData.email || '');
    formDataToSend.append('contact', formData.contact || '');
    formDataToSend.append('position_id', formData.position_id || '');
    formDataToSend.append('department_id', formData.department_id || '');
    formDataToSend.append('status', formData.status || '1');

    // Append optional fields
    if (formData.gender) formDataToSend.append('gender', formData.gender);
    if (formData.start_date) formDataToSend.append('start_date', formData.start_date);
    if (formData.duty_type) formDataToSend.append('duty_type', formData.duty_type);
    if (formData.pay_frequency) formDataToSend.append('pay_frequency', formData.pay_frequency);
    if (formData.additional_info) formDataToSend.append('additional_info', formData.additional_info);
    if (formData.image) formDataToSend.append('image', formData.image);

    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
        formDataToSend.append('updateId', updateId);
    }

    try {
        let response;

        if (postStatus === "ADD") {
            response = await employeesApiService.create(formDataToSend);
        } else if (postStatus === "UPDATE") {
            response = await employeesApiService.update(formDataToSend);
        } else if (postStatus === "DELETE") {
            response = await employeesApiService.delete(formDataToSend);
        }

        // Check success based on the service response
        if (response.success) {
            showToast("Success", response.message, "success");
            handleFormDataReset();
            handleDialogClose();
            fetchData();
        } else {
            showToast("Error", response.message, "error");
        }
    } catch (error) {
        console.log("error==>"+ JSON.stringify(error) )
        showToast("Error", "An unexpected error occurred \n\n" + error.message, "error");
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Employees Management ( {formatNumberWithComma2(pageState.pageData.length)} Total )
    </Typography>
    
    {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
        <Box sx={{ mt: 1, mb: 2 }}>
        <Chip
            label={`Selected Branch: ${getSelectedBranchName()}`}
            color="primary"
            variant="filled"
            onDelete={() => {
            setSelectedBranch('');
            fetchData();
            }}
            deleteIcon={<CloseIcon />}
        />
        </Box>
    )}

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Add New Employee
            </Button>
            )}

            {hasPermission(PERMISSIONS.BRANCHES) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={() => setBranchSelectionOpen(true)}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Select Branch
            </Button>
            )}
            
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={handlefilterOptions}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            View Options
            </Button>
            
            {hasPermission(PERMISSIONS.EXPORT) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Export Data
            </Button>
            )}
            
            <Button
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            Refresh
            </Button>
        </Box>

        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {(filters.employee_name || filters.dateFrom || filters.dateTo || filters.status || filters.department_id || filters.position_id) && (
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2"  sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.employee_name && (
            <Chip
                label={`Name: ${filters.employee_name}`}
                onDelete={() => setFilters(prev => ({ ...prev, employee_name: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.status && (
            <Chip
                label={`Status: ${getStatusById(filters.status)}`}
                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.department_id && (
            <Chip
                label={`Department: ${getCategoriesNameById(filters.department_id, departmentNamesData)}`}
                onDelete={() => setFilters(prev => ({ ...prev, department_id: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.position_id && (
            <Chip
                label={`Position: ${getCategoriesNameById(filters.position_id, positionNamesData)}`}
                onDelete={() => setFilters(prev => ({ ...prev, position_id: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateFrom && (
            <Chip 
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateTo && (
            <Chip 
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            <Button 
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon />}
            sx={{ ml: 1 }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Photo</TableCell>
                <TableCell>Employee Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Status</TableCell>
                {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((employee, index) => {
                const imageUrl = getImageUrl(employee);
                const initials = getInitials(employee.employee_name);
                const avatarColor = stringToColor(employee.employee_name);
                
                return (
                <TableRow
                    key={index}
                    hover
                    sx={{
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                    }
                    }}
                >
                    <TableCell>
                    <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    {imageUrl ? (
                        <Avatar
                        src={imageUrl}
                        alt={employee.employee_name}
                        sx={{ width: 40, height: 40 }}
                        />
                    ) : (
                        <Avatar
                        sx={{ 
                            width: 40, 
                            height: 40, 
                            bgcolor: avatarColor,
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                        >
                        {initials}
                        </Avatar>
                    )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                    {employee.employee_name}
                    {employee.gender && (
                        <Typography variant="caption" display="block" >
                        {employee.gender.charAt(0).toUpperCase() + employee.gender.slice(1)}
                        </Typography>
                    )}
                    </TableCell>
                    <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone fontSize="small" />
                        <Typography>{employee.contact}</Typography>
                    </Box>
                    </TableCell>
                    <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Email fontSize="small" />
                        <Typography variant="body2">{employee.email}</Typography>
                    </Box>
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={getCategoriesNameById(employee.position_id, positionNamesData)}
                        color="secondary"
                        variant="outlined"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={getCategoriesNameById(employee.department_id, departmentNamesData)}
                        color="info"
                        variant="outlined"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>
                    {employee.status === 1 ? (
                        <Chip
                        label={getStatusById(employee.status)}
                        size="small"
                        color="primary"
                        />
                    ) : (
                        <Chip
                        label={getStatusById(employee.status)}
                        size="small"
                        color="error"
                        />
                    )}
                    </TableCell>
                    {hasPermission(PERMISSIONS.BRANCHES) && (
                    <TableCell>
                        <Chip
                        label={getCategoriesNameById(employee.branch_id, branchNamesFilterData)}
                        color="warning"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    )}
                    <TableCell align="center">
                    <IconButton
                        edge="end"
                        onClick={() => handleOpenView(employee)}
                        color="primary"
                        sx={{
                        '&:hover': {
                            backgroundColor: theme.palette.info.light
                        }
                        }}
                    >
                        <ViewIcon />
                    </IconButton>
                    {hasPermission(PERMISSIONS.EDIT) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenEdit(employee)}
                        color="secondary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.success.light
                            }
                        }}
                        >
                        <EditIcon />
                        </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenDelete(employee)}
                        color="error"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.error.light
                            }
                        }}
                        >
                        <Delete />
                        </IconButton>
                    </>)}
                    </TableCell>
                </TableRow>
                )})}
            </TableBody>
            </Table>
        </TableContainer>

        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <People sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No employees found</Typography>
            <Typography variant="body2">Try adjusting your search query or add a new employee</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Branch Selection Dialog */}
    <Dialog
        open={branchSelectionOpen}
        onClose={() => setBranchSelectionOpen(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
        <DialogContent>
        <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
            <InputLabel id="branch-select-label">Select Branch</InputLabel>
            <Select
                labelId="branch-select-label"
                value={selectedBranch}
                label="Select Branch"
                onChange={(e) => handleBranchSelect(e.target.value)}
            >
                <MenuItem value="">
                <em>Select a branch</em>
                </MenuItem>
                {branchNamesFilterData.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                </MenuItem>
                ))}
            </Select>
            </FormControl>
            
            <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            <strong>Note:</strong> Selecting a branch will filter employees only from that branch. 
            Leave unselected to view all employees.
            </Typography>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setBranchSelectionOpen(false)}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={() => {
            if (selectedBranch) {
                fetchData(selectedBranch);
            }
            setBranchSelectionOpen(false);
            }}
            color="primary"
            variant="contained"
        >
            Apply
        </Button>
        </DialogActions>
    </Dialog>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
        }}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>

    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={handleCloseOptions}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Employees
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Employees
            </Button>

            <Divider>OR</Divider>

            <TextField
            fullWidth
            label="Employee Name"
            name="employee_name"
            value={filters.employee_name}
            onChange={handleFilterChange}
            />

            <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
            >
                <MenuItem value=""><em>All Status</em></MenuItem>
                {statusData.map((item, index) => (
                <MenuItem key={index} value={item.id}>
                    {item.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <FormControl fullWidth>
            <InputLabel>Department</InputLabel>
            <Select
                name="department_id"
                value={filters.department_id}
                onChange={handleFilterChange}
                label="Department"
            >
                <MenuItem value=""><em>All Departments</em></MenuItem>
                {departmentNamesData.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <FormControl fullWidth>
            <InputLabel>Position</InputLabel>
            <Select
                name="position_id"
                value={filters.position_id}
                onChange={handleFilterChange}
                label="Position"
            >
                <MenuItem value=""><em>All Positions</em></MenuItem>
                {positionNamesData.map((pos) => (
                <MenuItem key={pos.id} value={pos.id}>
                    {pos.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseOptions}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={handleCloseOptions}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        scroll="paper"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>
        <b>{postStatus === "ADD" ? "Add New Employee" : "Edit Employee"}</b>
        </DialogTitle>
        <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
            {/* Left Column - Personal Info */}
            <Grid item xs={12} md={6}>
                {/* Profile Picture */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                {imagePreview ? (
                    <Avatar
                    src={imagePreview}
                    sx={{ width: 120, height: 120, mb: 2 }}
                    />
                ) : (
                    <Avatar
                    sx={{ 
                        width: 120, 
                        height: 120, 
                        mb: 2,
                        bgcolor: formData.employee_name ? stringToColor(formData.employee_name) : theme.palette.primary.main,
                        fontSize: '36px',
                        fontWeight: 'bold'
                    }}
                    >
                    {getInitials(formData.employee_name)}
                    </Avatar>
                )}
                <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    size="small"
                >
                    Upload Photo
                    <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                    />
                </Button>
                <Typography variant="caption"  sx={{ mt: 1 }}>
                    Optional - JPG, PNG (Max 2MB)
                </Typography>
                </Box>

                <TextField
                fullWidth
                label="Employee Name *"
                name="employee_name"
                value={formData.employee_name}
                onChange={handleInputChange}
                error={errors.employee_name}
                helperText={errors.employee_name ? "Required" : ""}
                required
                sx={{ mb: 2 }}
                />

                <TextField
                fullWidth
                label="Email *"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
                helperText={errors.email ? "Required" : ""}
                required
                sx={{ mb: 2 }}
                />

                <TextField
                fullWidth
                label="Contact *"
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                error={errors.contact}
                helperText={errors.contact ? "Required" : ""}
                required
                sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Gender</InputLabel>
                <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    label="Gender"
                >
                    <MenuItem value=""><em>Select Gender</em></MenuItem>
                    {genderOptions.map((gender) => (
                    <MenuItem key={gender.id} value={gender.id}>
                        {gender.name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <TextField
                fullWidth
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
                />
            </Grid>

            {/* Right Column - Job Info */}
            <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Department *</InputLabel>
                <Select
                    name="department_id"
                    value={formData.department_id}
                    onChange={handleInputChange}
                    label="Department *"
                    error={errors.department_id}
                    required
                >
                    <MenuItem value=""><em>Select Department</em></MenuItem>
                    {departmentNamesData.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Position *</InputLabel>
                <Select
                    name="position_id"
                    value={formData.position_id}
                    onChange={handleInputChange}
                    label="Position *"
                    error={errors.position_id}
                    required
                >
                    <MenuItem value=""><em>Select Position</em></MenuItem>
                    {positionNamesData.map((position) => (
                    <MenuItem key={position.id} value={position.id}>
                        {position.name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Duty Type</InputLabel>
                <Select
                    name="duty_type"
                    value={formData.duty_type}
                    onChange={handleInputChange}
                    label="Duty Type"
                >
                    <MenuItem value=""><em>Select Duty Type</em></MenuItem>
                    {dutyTypeOptions.map((duty) => (
                    <MenuItem key={duty.id} value={duty.id}>
                        {duty.name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Pay Frequency</InputLabel>
                <Select
                    name="pay_frequency"
                    value={formData.pay_frequency}
                    onChange={handleInputChange}
                    label="Pay Frequency"
                >
                    <MenuItem value=""><em>Select Pay Frequency</em></MenuItem>
                    {payFrequencyOptions.map((pay) => (
                    <MenuItem key={pay.id} value={pay.id}>
                        {pay.name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Status *</InputLabel>
                <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status *"
                    error={errors.status}
                    required
                >
                    {statusData.map((item, index) => (
                    <MenuItem key={index} value={item.id}>
                        {item.name}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                {hasPermission(PERMISSIONS.BRANCHES) && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Branch *</InputLabel>
                    <Select
                    name="branch_id"
                    value={formData.branch_id}
                    onChange={handleInputChange}
                    label="Branch *"
                    required
                    >
                    <MenuItem value=""><em>Select Branch</em></MenuItem>
                    {branchNamesFilterData.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                        {branch.name} ({branch.code})
                        </MenuItem>
                    ))}
                    </Select>
                </FormControl>
                )}
            </Grid>

            {/* Full Width - Additional Info */}
            <Grid item xs={12}>
                <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Information"
                name="additional_info"
                value={formData.additional_info}
                onChange={handleInputChange}
                placeholder="Any additional information about the employee..."
                />
            </Grid>
            </Grid>

            <DialogActions sx={{ mt: 3, px: 0 }}>
            <Button
                color="error"
                onClick={handleCloseAdd}
                variant="contained"
            >
                Cancel
            </Button>
            <Button
                type="submit"
                color="primary"
                variant="contained"
            >
                {postStatus === "ADD" ? "Add Employee" : "Update Employee"}
            </Button>
            </DialogActions>
        </Box>
        </DialogContent>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Employee Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (
            <Box sx={{ p: 2 }}>
                {(() => {
                    const imageUrl = getImageUrl(selectedRecord);
                    const initials = getInitials(selectedRecord.employee_name);
                    const avatarColor = stringToColor(selectedRecord.employee_name);
                    
                    return (
                        <Grid container spacing={2}>
                            {/* Left Column - Profile */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    {imageUrl ? (
                                    <Avatar
                                        src={imageUrl}
                                        sx={{ width: 150, height: 150, mb: 2 }}
                                    />
                                    ) : (
                                    <Avatar
                                        sx={{ 
                                        width: 150, 
                                        height: 150, 
                                        mb: 2,
                                        bgcolor: avatarColor,
                                        fontSize: '48px',
                                        fontWeight: 'bold'
                                        }}
                                    >
                                        {initials}
                                    </Avatar>
                                    )}
                                    <Typography variant="h6" gutterBottom>
                                    {selectedRecord.employee_name}
                                    </Typography>
                                    <Chip
                                    label={selectedRecord.gender ? selectedRecord.gender.charAt(0).toUpperCase() + selectedRecord.gender.slice(1) : 'Not Specified'}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mb: 1 }}
                                    />
                                    <Chip
                                    label={getStatusById(selectedRecord.status)}
                                    color={selectedRecord.status === 1 ? "primary" : "error"}
                                    sx={{ mb: 2 }}
                                    />
                                </Box>
                            </Grid>

                            {/* Right Column - Details */}
                            <Grid item xs={12} md={8}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Email fontSize="small" />
                                        <Typography variant="subtitle2">
                                            <strong>Email:</strong> {selectedRecord.email}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Phone fontSize="small" />
                                        <Typography variant="subtitle2">
                                            <strong>Contact:</strong> {selectedRecord.contact}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Work fontSize="small" />
                                        <Typography variant="subtitle2">
                                            <strong>Position:</strong> {getCategoriesNameById(selectedRecord.position_id, positionNamesData)}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Business fontSize="small" />
                                        <Typography variant="subtitle2">
                                            <strong>Department:</strong> {getCategoriesNameById(selectedRecord.department_id, departmentNamesData)}
                                        </Typography>
                                    </Box>
                                    {selectedRecord.duty_type && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Work fontSize="small" />
                                            <Typography variant="subtitle2">
                                                <strong>Duty Type:</strong> {dutyTypeOptions.find(d => d.id === selectedRecord.duty_type)?.name || selectedRecord.duty_type}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {selectedRecord.pay_frequency && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Schedule fontSize="small" />
                                            <Typography variant="subtitle2">
                                                <strong>Pay Frequency:</strong> {payFrequencyOptions.find(p => p.id === selectedRecord.pay_frequency)?.name || selectedRecord.pay_frequency}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {selectedRecord.start_date && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarToday fontSize="small" />
                                            <Typography variant="subtitle2">
                                                <strong>Start Date:</strong> {new Date(selectedRecord.start_date).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {hasPermission(PERMISSIONS.BRANCHES) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Business fontSize="small" />
                                            <Typography variant="subtitle2">
                                                <strong>Branch:</strong> {getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData)}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CalendarToday fontSize="small" />
                                        <Typography variant="subtitle2">
                                            <strong>Created Date:</strong> {formatCreatedAtDate(selectedRecord.created_at)}
                                        </Typography>
                                    </Box>
                                    
                                    {selectedRecord.additional_info && (
                                        <>
                                        <Typography variant="subtitle2" gutterBottom>
                                            <strong>Additional Information:</strong>
                                        </Typography>
                                        <Typography variant="body1" sx={{ 
                                            p: 2, 
                                            bgcolor: 'action.hover', 
                                            borderRadius: 1,
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {selectedRecord.additional_info}
                                        </Typography>
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    );
                })()}
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        TransitionComponent={Transition}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Employee</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this employee?
        </Typography>
        {selectedRecord && (() => {
            const imageUrl = getImageUrl(selectedRecord);
            const initials = getInitials(selectedRecord.employee_name);
            const avatarColor = stringToColor(selectedRecord.employee_name);
            
            return (
            <Box sx={{ textAlign: 'center' }}>
                {imageUrl ? (
                <Avatar
                    src={imageUrl}
                    sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
                />
                ) : (
                <Avatar
                    sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    bgcolor: avatarColor,
                    fontSize: '24px',
                    fontWeight: 'bold'
                    }}
                >
                    {initials}
                </Avatar>
                )}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {selectedRecord.employee_name}
                </Typography>
                <Typography variant="body2"  gutterBottom>
                {selectedRecord.email}
                </Typography>
                <Typography variant="body2" gutterBottom>
                {getCategoriesNameById(selectedRecord.position_id, positionNamesData)}
                </Typography>
                {hasPermission(PERMISSIONS.BRANCHES) && (
                <Typography variant="body2">
                    Branch: {getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData) || 'N/A'}
                </Typography>
                )}
            </Box>
            );
        })()}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseDelete}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>
    </Container>
);
};

export default Employee;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});


